import { useRollbar } from '@rollbar/react'
import React, { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { QueryClientProvider } from '@tanstack/react-query'
import axios from 'axios'
import {
  fetchPageDataFailure,
  fetchPageDataSuccess,
} from 'client/actions/pageActions'
import {
  fetchEditorPage,
  fetchPageAutomationRulesData,
  fetchPageData,
} from 'client/api/pageApi'
import CodeDialog from 'client/components/core/CodeEditor/CodeEditorDialog'
import CommonErrorsDialog from 'client/components/core/ErrorsDialog/CommonErrorsDialog'
import ValidationErrorsDialog from 'client/components/core/ErrorsDialog/ValidationErrorsDialog'
import Toast from 'client/components/core/Toast'
import useBeforeunload from 'client/hooks/useBeforeunload'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { useWebsitePages } from 'client/store'
import { fetchPageAutomationRules } from 'client/store/automationRules/automationRulesActions'
import { migratePage } from 'client/utils/migratePage'
import { AccessDenied } from 'common/errors/AccessDenied'
import { NetworkError } from 'common/errors/NetworkError'
import { NotFound } from 'common/errors/NotFound'
import { useActions } from 'common/hooks/useActions'
import toast from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import { getWebsitePageById } from 'src/editor/api/website-pages-api'
import { useApplyUserLocale } from 'src/editor/hooks/use-apply-user-locale'
import { useSentReduxLogsToRollbar } from 'src/editor/hooks/use-sent-redux-logs-to-rollbar'
import { Layout } from 'src/editor/layout'
import { Content } from 'src/editor/layout/content'
import { WebsiteContent } from 'src/editor/layout/website-content'
import { getWebsiteLayoutPageIdForLocale } from 'src/editor/shared/utils/get-website-layout-page-id'
import { queryClient } from 'src/editor/shared/utils/queryClient'
import { fetchWebsitePageDataSuccess } from 'src/editor/store/website-pages/website-pages-actions'
import { getIsPreviewMode } from 'src/editor/store/website-pages/website-pages-selector'
import * as managementActions from './actions/actionsManagement'
import { DeviceModeProvider } from './context/DeviceModeContext'
import { HighlightedEntityProvider } from './context/HighlightedContext'
import SidebarVisibilityContextProvider from './context/SidebarVisibilityContext'
import { locales } from './i18n'

function AppNew() {
  if (process.env.NODE_ENV !== 'development') {
    // biome-ignore lint/correctness/useHookAtTopLevel: was here before, not sure if needed
    useBeforeunload(e => e.preventDefault())
  }
  const { i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const dispatchFetchPageData = useActions(fetchPageDataSuccess)
  const dispatchFetchWebsitePage = useActions(fetchWebsitePageDataSuccess)
  const dispatch = useDispatch()
  const isWebsitePreview = useWebsitePages(getIsPreviewMode)
  const rollbar = useRollbar()
  const dispatchFetchPageDataFail = useActions(fetchPageDataFailure)
  const setHasLostEntities = useActions(managementActions.setHasLostEntities)
  const setHasMasterBlockRootWithoutMasterBlockId = useActions(
    managementActions.setHasMasterBlockRootWithoutMasterBlockId,
  )
  const setHasMasterBlockIdsWithoutMasterRoot = useActions(
    managementActions.setHasMasterBlockIdsWithoutMasterRoot,
  )
  const { pageId, isTemplate, isWebsite } = usePageRouteDetector()
  const { isUserFetched, user } = useApplyUserLocale()

  useSentReduxLogsToRollbar()

  useEffect(() => {
    const browserLanguage = window.navigator.languages.find(lng =>
      locales.includes(lng),
    )
    const fixedT = i18n.getFixedT(browserLanguage)

    async function fetchPage(pageId) {
      setIsLoading(true)
      try {
        if (isWebsite) {
          const websiteData = await getWebsitePageById(pageId)

          const layoutPageId = getWebsiteLayoutPageIdForLocale({
            layouts: websiteData.layouts,
            currentLocale: websiteData.locale,
          })

          const mergedWebsitePage = await fetchEditorPage(pageId, layoutPageId)
          dispatchFetchWebsitePage({
            ...websiteData,
            layoutPageId,
          })
          dispatchFetchPageData(mergedWebsitePage, rollbar)
        } else {
          const response = await fetchPageData(pageId, isTemplate)
          try {
            response.data.page = migratePage(response.data.page)
          } catch (error) {
            rollbar.error(error)
          }
          dispatchFetchPageData(response.data, rollbar)
        }

        setIsLoading(false)
      } catch (error) {
        // it's a way to show to QA and devs that the page is broken
        if (
          process.env.APP_ENV === 'staging' &&
          axios.isAxiosError(error) &&
          error.response.status === 400
        ) {
          toast.error(error.response.data.message, { duration: 2000 })
          return
        }

        // the code below won't work since no axios interceptor was applied
        // todo we need to handle 500 and 401
        if (error instanceof AccessDenied) {
          // do nothing
        } else if (error instanceof NotFound) {
          dispatchFetchPageDataFail(fixedT('core.errors.page_not_found'))
        } else if (error instanceof NetworkError) {
          dispatchFetchPageDataFail(fixedT('core.errors.no_connection'))
        } else {
          dispatchFetchPageDataFail(
            fixedT('core.errors.went_wrong_contact_support'),
          )
          rollbar.error(error)
          setIsError(true)
        }
      }
    }

    async function fetchAutomationRules(pageId) {
      try {
        const response = await fetchPageAutomationRulesData(pageId, isTemplate)
        dispatch(fetchPageAutomationRules(response.data))
        // biome-ignore lint/correctness/noUnusedVariables: <explanation>
      } catch (error) {
        // do nothing
      }
    }

    if (!pageId) {
      dispatchFetchPageDataFail(fixedT('core.errors.page_not_found'))
    } else {
      if (!isUserFetched || !user) {
        return
      }

      fetchPage(pageId)
      if (!isTemplate) {
        fetchAutomationRules(pageId)
      }
    }
  }, [
    dispatch,
    dispatchFetchPageData,
    dispatchFetchPageDataFail,
    i18n,
    isTemplate,
    setHasLostEntities,
    setHasMasterBlockIdsWithoutMasterRoot,
    setHasMasterBlockRootWithoutMasterBlockId,
    isUserFetched,
    user,
  ])

  const loading = isLoading || !isUserFetched

  return (
    <QueryClientProvider client={queryClient}>
      <SkeletonTheme color="#F2F2F380" highlightColor="#F2F2F3">
        <SidebarVisibilityContextProvider>
          <DeviceModeProvider>
            <HighlightedEntityProvider>
              <Layout isWebsitePreview={isWebsitePreview}>
                {isWebsitePreview ? (
                  <WebsiteContent />
                ) : (
                  !isError && <Content isLoading={loading} />
                )}
                <ValidationErrorsDialog />
                <CommonErrorsDialog />
                <CodeDialog />
                <Toast />
              </Layout>
            </HighlightedEntityProvider>
          </DeviceModeProvider>
        </SidebarVisibilityContextProvider>
      </SkeletonTheme>
    </QueryClientProvider>
  )
}

export default memo(AppNew)
